import { createSelector } from 'reselect';
import { get, find, at, filter, map } from 'lodash/fp';
import { getStringArrayProp, getStringProp, values } from '@mews/fp';
import { Taxation, TaxRateStrategyDiscriminator, TaxRateStrategyCoProduct } from '@mews-commander/types';
import { Option } from '@mews-commander/legacy-data-structures';
import { getContainer } from '@mews-commander/legacy-app-instance';
import { TaxEnvironment } from '@mews-commander/data-access';
import { getTaxations, getTaxEnvironments } from '@mews-commander/core/legacy-globalization';

export const getTaxEnvironment = createSelector(getStringProp('taxEnvironmentCode'), getTaxEnvironments, get);

const getTaxEnvironmentTaxationCodes = createSelector(getTaxEnvironment, get('taxationCodes'));

// @ts-expect-error $TSFixMeError: No overload matches this call.
const getTaxEnvironmentTaxations = createSelector(getTaxEnvironmentTaxationCodes, getTaxations, at);

export const getTaxationsByTaxEnvironmentCode = createSelector(
    getTaxations,
    getTaxEnvironmentTaxationCodes,
    (taxations, taxationCodes) => filter((taxation) => taxationCodes.includes((taxation as $TSFixMe).code), taxations)
);

export const getTaxEnvironmentTaxationByTaxRateCode = createSelector(
    getStringProp('taxRateCode'),
    getTaxEnvironmentTaxations,
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    (taxRateCode, taxations) => find(({ taxRates }) => find({ code: taxRateCode }, taxRates))(taxations)
);

export const getTaxEnvironmentTaxRate = createSelector(
    getStringProp('taxRateCode'),
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    getTaxEnvironmentTaxationByTaxRateCode,
    (taxRateCode, { taxRates }) => find({ code: taxRateCode }, taxRates)
);

export const getTaxEnvironmentsByTaxEnvironmentCodes = createSelector(
    getTaxEnvironments,
    getStringArrayProp('taxEnvironmentCodes'),
    (taxEnvironments, taxEnvironmentCodes) => {
        const taxEnvironmentsList: TaxEnvironment[] = values(taxEnvironments);

        return taxEnvironmentsList.filter(({ code }) => taxEnvironmentCodes.includes(code));
    }
);

export const getTaxationsByTaxationCodes = createSelector(
    getTaxations,
    getStringArrayProp('taxationCodes'),
    (taxations, taxationCodes) => {
        const taxationList: Taxation[] = values(taxations);

        return taxationList.filter(({ code }) => taxationCodes.includes(code));
    }
);

export const getTaxationByTaxRateCode = createSelector(
    getTaxations,
    getStringArrayProp('taxRateCodes'),
    (taxations, taxRateCodes) => {
        const taxationList: Taxation[] = values(taxations);

        return taxationList.reduce(
            (acc, taxation) => {
                taxation.taxRates.forEach((rate) => {
                    if (taxRateCodes.includes(rate.code)) {
                        acc[rate.code] = {
                            name: taxation.name,
                            localName: taxation.localName,
                            taxStrategy: rate.strategy,
                        };
                    }
                });
                return acc;
            },
            {} as Record<string, { name: string; localName: string; taxStrategy: TaxRateStrategyCoProduct }>
        );
    }
);

export const getLocalizedTaxRateOptionLabel = (taxRateStrategy: $TSFixMe) =>
    createSelector(getContainer, (container) => {
        const { appLocalizationComponent } = container();
        const { localizeCurrencyValue, localizePercent } = appLocalizationComponent;
        switch (taxRateStrategy.discriminator) {
            case TaxRateStrategyDiscriminator.Flat: {
                return localizeCurrencyValue(
                    {
                        value: taxRateStrategy.value.value,
                        currency: taxRateStrategy.value.currencyCode,
                    },
                    { showSymbol: false }
                );
            }
            default: {
                return localizePercent(taxRateStrategy.value.value, { showSymbol: false });
            }
        }
    });

export const getLocalizedTaxRatesOptions = map((taxRate) =>
    Option({
        value: (taxRate as $TSFixMe).code,
        // @ts-expect-error $TSFixMeError: Expected 1 arguments, but got 0.
        label: getLocalizedTaxRateOptionLabel((taxRate as $TSFixMe).strategy)(),
    })
);
